import * as _ from "lodash";

import { types } from "@/actions/map";

const valuesInitialState = {
    lights: {},
    deletedLights: [],
    markers: {},
    deletedMarkers: [],
    groupGeometries: [],
};

const sortGroupGeometries = (geometries) =>
    geometries.sort((a, b) => (a.zIndex ?? 0) - (b.zIndex ?? 0));

/**
 * Reducer for the global light values including lights, deletions, and group geometries.
 *
 * @param state - The current global values state.
 * @param root0 - The dispatched action.
 * @param root0.payload - The action payload.
 * @param root0.type - The action type.
 * @returns The next global values state.
 */
const globalReducer = (state = valuesInitialState, { payload, type }) => {
    switch (type) {
        case types.VALUES.GLOBAL.CLEAR_DELETED_LIGHTS:
            return {
                ...state,
                deletedLights: [],
            };
        case types.VALUES.GLOBAL.UPDATE_LIGHTS:
        case types.VALUES.GLOBAL.UPDATE_LIGHTS_SILENT:
            _.forIn(payload, (l) => {
                l.changed = true;
            });
            return {
                ...state,
                lights: _.cloneDeep({
                    ...state.lights,
                    ...payload,
                }),
            };
        case types.VALUES.GLOBAL.SET_LIGHTS:
            return {
                ...state,
                lights: _.cloneDeep(
                    payload.reset
                        ? payload.lights
                        : { ...state.lights, ...payload.lights },
                ),
            };
        case types.VALUES.GLOBAL.UPDATE_DELETE_LIGHTS:
            return {
                ...state,
                deletedLights: [...state.deletedLights, ...payload],
            };
        case types.VALUES.GLOBAL.DELETE_LIGHTS:
            return {
                ...state,
                deletedLights: [...state.deletedLights, ...payload],
                lights: _.cloneDeep(
                    _.omitBy(state.lights, (light: { uniqueId?: string }) =>
                        _.some(
                            payload,
                            (deletedLight: { uniqueId?: string }) =>
                                deletedLight.uniqueId === light.uniqueId,
                        ),
                    ),
                ),
            };
        case types.VALUES.GLOBAL.ADD_GROUP_GEOMETRY:
            return {
                ...state,
                groupGeometries: sortGroupGeometries([
                    ...(state.groupGeometries ?? []).filter(
                        (geometry) => geometry.id !== payload.id,
                    ),
                    payload,
                ]),
            };
        case types.VALUES.GLOBAL.DELETE_GROUP_GEOMETRY:
            return {
                ...state,
                groupGeometries: sortGroupGeometries(
                    state.groupGeometries.filter((g) => g.id !== payload),
                ),
            };
        case types.VALUES.GLOBAL.SET_GROUP_GEOMETRIES:
            return {
                ...state,
                groupGeometries: sortGroupGeometries(payload),
            };
        case types.VALUES.GLOBAL.SET_MARKERS:
            return {
                ...state,
                markers: payload,
            };
        case types.VALUES.GLOBAL.UPDATE_MARKERS:
            _.forIn(payload, (m) => {
                m.changed = true;
            });
            return {
                ...state,
                markers: {
                    ...state.markers,
                    ...payload,
                },
            };
        case types.VALUES.GLOBAL.DELETE_MARKERS:
            return {
                ...state,
                deletedMarkers: [...state.deletedMarkers, ...payload],
                markers: _.omitBy(
                    state.markers,
                    (marker: { uniqueId?: string }) =>
                        _.some(
                            payload,
                            (deleted: { uniqueId?: string }) =>
                                deleted.uniqueId === marker.uniqueId,
                        ),
                ),
            };
        case types.VALUES.GLOBAL.CLEAR_DELETED_MARKERS:
            return {
                ...state,
                deletedMarkers: [],
            };
        default:
            return state;
    }
};

export default globalReducer;
